import React, { JSX, useCallback, useMemo } from "react";
import "./DashboardReportWrapper.scss";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { DateUtils } from "utils/time-utils";
import {
  DashboardDateVariable,
  DashboardHtmlVariable,
  DashboardReportMarginBoxes,
  DashboardVariable,
} from "api/model/dashboard/DashboardReportOptions";
import { I } from "services/lang/LanguageService";
import { JodaTimeUtils } from "utils/joda-time-utils";
import {
  getDashboardComponentState,
  getDashboardState,
  getFinishedDashboardComponents,
  setDashboardState,
  storeDashboardComponentState,
} from "store/window-dashboard-store";
import { ChronoUnit, DateTimeFormatter, Instant, ZoneId } from "@js-joda/core";
import { IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { ComponentGroupLayoutWrapper } from "../dashboard-report/DashboardReportPage";
import {
  DashboardComponentMappingWrapper,
  getFullComponentId,
} from "../dashboard/components/dashboard-utils";
import { DashboardComponentState } from "../dashboard/components/interfaces";
import { getComponentType } from "../../ComponentType";
import truncateAndOffsetLocalDatetime = JodaTimeUtils.truncateAndOffsetLocalDatetime;

declare global {
  interface Window {
    reportGenerationOptions?: {
      companyRef: string;
      dashboardId: string;
      dashboardTitle: string;
      referenceTime: string;
      easasUrl: string;
      scheduleTime: string | null;
      genStartTime: string;
    };
  }
}

type ReportWrapperProps = {
  dashboard: Dashboard;
  referenceTime: string;
  children: JSX.Element | JSX.Element[];
};

type DashboardReportComponentProps = {
  header: string;
  children: JSX.Element;
};

const DashboardReportWrapper = (props: {
  dashboard: Dashboard;
  referenceTime: string;
  finishCallback: (status: "finished" | "error") => void;
}) => {
  // All dashboard components with callback support
  const supportedComponents = useMemo(() => {
    const components: Record<string, IDashboardComponent> = {};
    props.dashboard.dashboardTabsJs?.forEach((t, tabIndex) => {
      t.componentGroups?.forEach((g, groupIndex) => {
        g.components?.forEach((c, componentIndex) => {
          const typeMapped = getComponentType(c.type);
          if (typeMapped?.supportsStatusCallback) {
            const stringId = getFullComponentId(c.type, tabIndex, groupIndex, componentIndex);
            components[stringId] = c;
          }
        });
      });
    });
    return components;
  }, [props.dashboard.dashboardTabsJs]);

  const componentIds = Object.keys(supportedComponents);

  // All dashboard components with callback support
  const componentCount = Object.keys(supportedComponents).length;

  // True if all supported components are finished
  const allFinished = () =>
    Object.keys(supportedComponents).every(id => {
      const state = getDashboardComponentState(id);
      return state?.state === "finished";
    });

  const componentStateCb = useCallback(
    (state: DashboardComponentState) => {
      storeDashboardComponentState(state);

      // Currently finished components
      const finishedCount = getFinishedDashboardComponents(componentIds);

      // Calculate percentage of finished components
      const percent = Math.round((finishedCount / componentCount) * 100);
      console.log(
        `Status of dashboard: ${state.id} -> ${state.state} (${finishedCount}/${componentCount} = ${percent}% finished)`
      );

      // Short circuit if dashboard is already finished or errored
      if (getDashboardState() === "finished" || getDashboardState() === "error") return;

      // Check if component errored
      if (state.state === "error") {
        props.finishCallback("error");
        setDashboardState("error");
      }

      // If all components are finished, set dashboard state to finished
      if (allFinished()) {
        props.finishCallback("finished");
        setDashboardState("finished");
      }
    },
    [props.dashboard]
  );

  return (
    <ReportWrapper dashboard={props.dashboard} referenceTime={props.referenceTime}>
      {props.dashboard.dashboardTabsJs?.map((c, tabIdx) => {
        return (
          <div key={c.uuid} className={tabIdx === 0 ? "" : `report-tab-break`}>
            <React.Fragment key={c.uuid}>
              {c.componentGroups?.map((g, groupIdx) => {
                return (
                  <ComponentGroupLayoutWrapper key={g.id} group={g}>
                    {g.components?.map((component, componentIdx) => {
                      return (
                        <React.Fragment key={component.id}>
                          <DashboardReportComponent header={I(component.title)}>
                            <DashboardComponentMappingWrapper
                              id={getFullComponentId(
                                component.type,
                                tabIdx,
                                groupIdx,
                                componentIdx
                              )}
                              component={component.type}
                              config={component.config}
                              referenceTime={props.referenceTime}
                              stateCb={componentStateCb}
                            />
                          </DashboardReportComponent>
                        </React.Fragment>
                      );
                    })}
                  </ComponentGroupLayoutWrapper>
                );
              })}
            </React.Fragment>
          </div>
        );
      })}
    </ReportWrapper>
  );
};

const ReportWrapper = ({ children, dashboard, referenceTime }: ReportWrapperProps) => {
  const DEFAULT_PATTERN = "yyyy-MM-dd HH:mm";
  const { marginBoxes } = dashboard.reportOptions;

  const processVariables = (html: string) => {
    const pattern = dashboard.reportOptions.timingOptions.reportTimeFormat ?? DEFAULT_PATTERN;

    const scheduleTimeReplacer = () => {
      if (!window.reportGenerationOptions?.scheduleTime) return "NOT-SCHEDULED";
      return DateUtils.strFormatDateTime(window.reportGenerationOptions?.scheduleTime, pattern);
    };

    const generationTimeReplacer = () => {
      if (!window.reportGenerationOptions?.genStartTime) return "NOT-GENERATED";
      return DateUtils.strFormatDateTime(window.reportGenerationOptions?.genStartTime, pattern);
    };

    const referenceTimeReplacer = () => {
      return DateUtils.strFormatDateTime(referenceTime, pattern);
    };

    const replacer: Record<string, string> = {
      scheduleTime: scheduleTimeReplacer(),
      referenceTime: referenceTimeReplacer(),
      generationTime: generationTimeReplacer(),
      totalPages: '<span class="page-number-total"></span>',
      currentPage: '<span class="page-number-current"></span>',
    };

    let processed = html;
    Object.keys(replacer).forEach(kv => {
      const value = replacer[kv];
      const regex = new RegExp(`\\$${kv}`, "g");
      processed = processed.replace(regex, value);
    });

    return processed;
  };

  const variableValueMapper = (value: DashboardVariable, currentDashboard: Dashboard) => {
    switch (value.type) {
      case "DATE": {
        const defaults: Partial<DashboardDateVariable> = {
          dateType: "GENERATION_TIME",
          truncateTo: ChronoUnit.HOURS.toString(),
          offsetUnit: ChronoUnit.HOURS.toString(),
          offset: 0,
          format: DEFAULT_PATTERN,
        };

        let dateObj = value as DashboardDateVariable;
        // apply defaults
        dateObj = { ...defaults, ...dateObj };

        const zoneId = ZoneId.of(currentDashboard.reportOptions.timingOptions.timeZoneId);
        let date: any;
        switch (dateObj.dateType) {
          case "GENERATION_TIME":
            date = Instant.now().atZone(zoneId).toLocalDateTime();
            break;
          case "REFERENCE_TIME":
            date = Instant.parse(referenceTime).atZone(zoneId).toLocalDateTime();
            break;
          default:
            throw new Error(`Unsupported date type: ${dateObj.dateType}`);
        }

        date = truncateAndOffsetLocalDatetime(
          date,
          dateObj.truncateTo!!,
          dateObj.offset!!,
          dateObj.offsetUnit!!
        );

        return date.format(DateTimeFormatter.ofPattern(dateObj.format!!));
      }
      case "HTML": {
        const htmlObj = value as DashboardHtmlVariable;
        return htmlObj.content;
      }
      default:
        throw new Error(`Unsupported variable type: ${value.type}`);
    }
  };

  /**
   * Variables prefixed with $ are replaced with builtin values or values from dashboard.reportOptions.variables
   * @param marginBox margin box name to process
   */
  const preformatMarginBox = (marginBox: keyof DashboardReportMarginBoxes) => {
    const box = marginBoxes?.[marginBox];
    const html = box?.content;
    if (!html) return undefined;

    // Fixed variables processing
    // TODO: when there is more builtin values make replacer map struct to cleanup this code
    let processed = processVariables(html);

    // Margin box dynamic variables processing
    const boxVariables = box.variables;
    if (boxVariables) {
      Object.keys(boxVariables).forEach(kv => {
        const value = boxVariables[kv];
        const regex = new RegExp(`\\$${kv}`, "g");
        processed = processed.replace(regex, value);
      });
    }

    // Global variables processing
    const globalVariables = dashboard.reportOptions.variables;
    if (globalVariables) {
      Object.keys(globalVariables).forEach(kv => {
        const objValue = globalVariables[kv];
        const regex = new RegExp(`\\$${kv}`, "g");
        const value = variableValueMapper(objValue, dashboard);
        processed = processed.replace(regex, value);
      });
    }

    return { __html: I(processed) };
  };

  return (
    <div
      id="dashboard-report-wrapper-paged"
      className="dashboard-report-wrapper-paged print-mode-enabled"
    >
      {/* Margin Boxes */}
      <div className="topLeftMarginBox" dangerouslySetInnerHTML={preformatMarginBox("topLeft")} />
      <div
        className="topCenterMarginBox"
        dangerouslySetInnerHTML={preformatMarginBox("topCenter")}
      />
      <div className="topRightMarginBox" dangerouslySetInnerHTML={preformatMarginBox("topRight")} />
      <div
        className="bottomLeftMarginBox"
        dangerouslySetInnerHTML={preformatMarginBox("bottomLeft")}
      />
      <div
        className="bottomCenterMarginBox"
        dangerouslySetInnerHTML={preformatMarginBox("bottomCenter")}
      />
      <div
        className="bottomRightMarginBox"
        dangerouslySetInnerHTML={preformatMarginBox("bottomRight")}
      />

      {/* Dashboad Components */}
      {children}
    </div>
  );
};

const useBorders = (componentType: string, theme?: string) => {
  if (theme === "dark" && componentType !== "FreeText" && componentType !== "Separator") {
    return "component-border";
  }
  return "";
};

const DashboardReportComponent = ({ header, children }: DashboardReportComponentProps) => {
  return (
    <div
      className={`dashboard-report-component ${children.props.config.theme ?? "light"} ${useBorders(children.props.component, children.props.config.theme)} `}
    >
      {header && (
        <div className="dashboard-report-component-header">
          <h3>{I(header)}</h3>
        </div>
      )}

      <div className="dashboard-report-component-body">{React.cloneElement(children)}</div>
    </div>
  );
};

export default DashboardReportWrapper;
